/**
 * SPEC-location-fields §5.2 — parts-equality dedup.
 * Port of v0 lib/brand-bible-data.ts normalize / partsEqual / pickKeepDrop /
 * findDuplicatePairs. Flags pairs. Never auto-merges.
 *
 * Completeness guard is production-only: two empty drafts must not flag.
 */

export interface AddressParts {
  street?: string | null;
  suite?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

export interface MergeRow extends AddressParts {
  id: string;
  locationKey: string;
  verified: boolean;
  createdAt?: string | null;
}

/** v0 normalize: trim, case-fold, strip trailing commas, collapse whitespace. */
export function normalize(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/,+$/, '')
    .replace(/\s+/g, ' ');
}

/** Alias used by existing callers / tests. */
export function normalizePart(value: string | null | undefined): string {
  return normalize(value);
}

export function partsKey(parts: AddressParts): string {
  return ['street', 'suite', 'city', 'state', 'zip']
    .map((k) => normalize(parts[k as keyof AddressParts]))
    .join('\u0001');
}

/** Rebuild the legacy `address` string from typed parts (phase-1 PATCH target). */
export function composeAddress(parts: AddressParts): string {
  const stateZip = [parts.state?.trim(), parts.zip?.trim()].filter(Boolean).join(' ');
  return [parts.street, parts.suite, parts.city, stateZip]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean)
    .join(', ');
}

/**
 * Equal iff the five typed parts match after v0 normalize.
 * Incomplete rows (missing street/city/state/zip) never flag.
 */
export function partsEqual(a: AddressParts, b: AddressParts): boolean {
  const street = normalize(a.street);
  const city = normalize(a.city);
  const state = normalize(a.state);
  const zip = normalize(a.zip);
  if (!street || !city || !state || !zip) return false;
  return (
    street === normalize(b.street) &&
    normalize(a.suite) === normalize(b.suite) &&
    city === normalize(b.city) &&
    state === normalize(b.state) &&
    zip === normalize(b.zip)
  );
}

export interface KeepDrop {
  keepId: string;
  dropId: string;
}

/**
 * v0 pickKeepDrop: verified wins; if neither (or both) verified, older createdAt
 * wins. Missing timestamps keep `a`.
 */
export function pickKeepDrop<T extends { id: string; verified: boolean; createdAt?: string | null }>(
  a: T,
  b: T,
): KeepDrop {
  if (a.verified && !b.verified) return { keepId: a.id, dropId: b.id };
  if (b.verified && !a.verified) return { keepId: b.id, dropId: a.id };
  const aTime = a.createdAt ?? '';
  const bTime = b.createdAt ?? '';
  if (aTime && bTime) {
    const older = aTime <= bTime ? a : b;
    const other = older === a ? b : a;
    return { keepId: older.id, dropId: other.id };
  }
  return { keepId: a.id, dropId: b.id };
}

/** Existing-test wrapper: same winner as pickKeepDrop, returns the row. */
export function pickKeepRow<T extends { id: string; verified: boolean; createdAt?: string | null }>(
  a: T,
  b: T,
): T {
  const { keepId } = pickKeepDrop(a, b);
  return keepId === a.id ? a : b;
}

export interface DuplicatePair<T extends MergeRow = MergeRow> {
  keep: T;
  twin: T;
}

/** v0 pairwise scan. Returns { keep, twin } so existing tests stay green. */
export function findDuplicatePairs<T extends MergeRow>(rows: T[]): DuplicatePair<T>[] {
  const pairs: DuplicatePair<T>[] = [];
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      if (!partsEqual(rows[i], rows[j])) continue;
      const { keepId, dropId } = pickKeepDrop(rows[i], rows[j]);
      const keep = keepId === rows[i].id ? rows[i] : rows[j];
      const twin = dropId === rows[i].id ? rows[i] : rows[j];
      pairs.push({ keep, twin });
    }
  }
  return pairs;
}
